import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { jwtDecode } from 'jwt-decode';

import { STATUS_COLUMNS, TYPE_OPTIONS, PRIORITY_OPTIONS } from './ticketConstants';
import TicketCard from './TicketCard';
import CreateTicketDialog from './CreateTicketDialog';
import TicketDetailDialog from './TicketDetailDialog';
import { getDevTickets } from '../../collections/devTickets';

const KanbanBoard = (props) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [currentUser, setCurrentUser] = useState({ id: null, role: null });

  useEffect(() => {
    if (props.setPageTitle) {
      props.setPageTitle('Yêu cầu phát triển');
    }
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setCurrentUser({ id: decoded.id, role: decoded.role });
      } catch (e) {
        // ignore
      }
    }
  }, [props]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const res = await getDevTickets(params);
      setTickets(res.data || []);
    } catch (err) {
      console.error('Failed to load tickets', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, priorityFilter]);

  const ticketsByStatus = useMemo(() => {
    const grouped = {};
    STATUS_COLUMNS.forEach((c) => {
      grouped[c.key] = [];
    });
    tickets.forEach((t) => {
      if (grouped[t.status]) {
        grouped[t.status].push(t);
      } else {
        grouped[t.status] = [t];
      }
    });
    return grouped;
  }, [tickets]);

  const isAdmin = currentUser.role === 'ADMIN';

  const handleCreated = (newTicket) => {
    setTickets((prev) => [newTicket, ...prev]);
  };

  const handleChanged = (updatedTicket) => {
    setTickets((prev) =>
      prev.map((t) => (t.id === updatedTicket.id ? { ...t, ...updatedTicket } : t))
    );
  };

  const handleDeleted = (deletedId) => {
    setTickets((prev) => prev.filter((t) => t.id !== deletedId));
  };

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={2} flexWrap="wrap">
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Yêu cầu phát triển</Typography>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Loại</InputLabel>
          <Select
            label="Loại"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="">Tất cả</MenuItem>
            {TYPE_OPTIONS.map((t) => (
              <MenuItem key={t.key} value={t.key}>{t.emoji} {t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Ưu tiên</InputLabel>
          <Select
            label="Ưu tiên"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <MenuItem value="">Tất cả</MenuItem>
            {PRIORITY_OPTIONS.map((p) => (
              <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Tạo yêu cầu
        </Button>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 2,
          }}
        >
          {STATUS_COLUMNS.map((col) => (
            <Paper
              key={col.key}
              variant="outlined"
              sx={{ p: 1.5, background: '#f7f7f7', minHeight: 300 }}
            >
              <Stack direction="row" alignItems="center" mb={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {col.label}
                </Typography>
                <Box flexGrow={1} />
                <Typography variant="caption" color="text.secondary">
                  {ticketsByStatus[col.key]?.length || 0}
                </Typography>
              </Stack>
              <Box>
                {(ticketsByStatus[col.key] || []).map((ticket) => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onClick={(t) => setSelectedTicketId(t.id)}
                  />
                ))}
                {(!ticketsByStatus[col.key] || ticketsByStatus[col.key].length === 0) && (
                  <Typography variant="caption" color="text.secondary">
                    Chưa có yêu cầu
                  </Typography>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <CreateTicketDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
      <TicketDetailDialog
        open={!!selectedTicketId}
        ticketId={selectedTicketId}
        onClose={() => setSelectedTicketId(null)}
        onChanged={handleChanged}
        onDeleted={handleDeleted}
        isAdmin={isAdmin}
        currentAccountId={currentUser.id}
      />
    </Container>
  );
};

export default KanbanBoard;
